import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ConfirmDialogService } from '../../../services/confirm-dialog-service/confirm-dialog.service';
import {
    OrderdirectInvoice,
    OrderdirectInvoiceService,
} from '../../../services/orderdirect-invoice/orderdirect-invoice.service';

@Component({
    selector: 'app-orderdirect-invoices',
    templateUrl: './orderdirect-invoices.component.html',
    styleUrls: ['./orderdirect-invoices.component.scss'],
    standalone: false,
})
export class OrderdirectInvoicesComponent implements OnInit, OnDestroy {
    public invoices: OrderdirectInvoice[] = [];
    public loading = false;
    public pageSize = 25;
    public pageIndex = 0;
    public totalItems = 0;
    public pageSizeOptions = [10, 25, 50, 100];
    public statusFilter: 'new' | 'pending' | 'completed' | 'failed' | null = null;
    public fcSearch = new FormControl('');
    private subscriptions = new Subscription();

    constructor(
        private invoiceService: OrderdirectInvoiceService,
        private confirmDialog: ConfirmDialogService
    ) {}

    ngOnInit() {
        this.loadInvoices();

        this.subscriptions.add(
            this.fcSearch.valueChanges.pipe(debounceTime(300)).subscribe(() => {
                this.pageIndex = 0;
                this.loadInvoices();
            })
        );
    }

    triggerSync() {
        this.subscriptions.add(
            this.invoiceService.triggerSync().subscribe(() => {
                this.confirmDialog.confirm({
                    title: 'Synchronisatie',
                    text: 'Er wordt een nieuwe synchronisatie uitgevoerd met Order Direct',
                    confirm: 'Sluiten',
                    cancel: null,
                });
            })
        );
    }

    loadInvoices() {
        this.loading = true;
        const params: any = {
            per_page: this.pageSize,
            page: this.pageIndex + 1, // Laravel uses 1-based pagination
        };

        if (this.statusFilter) {
            params.status = this.statusFilter;
        }

        if (this.fcSearch.value) {
            params.search = this.fcSearch.value;
        }

        this.subscriptions.add(
            this.invoiceService.getInvoices(params).subscribe({
                next: response => {
                    this.invoices = response.data.data;
                    this.totalItems = response.data.total;
                    this.loading = false;
                },
                error: error => {
                    console.error('Error loading invoices:', error);
                    this.loading = false;
                },
            })
        );
    }

    filterByStatus(status: 'new' | 'pending' | 'completed' | 'failed' | null) {
        this.statusFilter = status;
        this.pageIndex = 0;
        this.loadInvoices();
    }

    onPageChange(event: PageEvent) {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.loadInvoices();
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'completed':
                return 'badge-success';
            case 'pending':
                return 'badge-warning';
            case 'failed':
                return 'badge-danger';
            case 'new':
                return 'badge-info';
            default:
                return 'badge-secondary';
        }
    }

    getStatusLabel(status: string): string {
        switch (status) {
            case 'completed':
                return 'Voltooid';
            case 'pending':
                return 'In behandeling';
            case 'failed':
                return 'Mislukt';
            case 'new':
                return 'Nieuw';
            default:
                return status;
        }
    }

    sendToAfas(invoice: OrderdirectInvoice, event: Event) {
        event.stopPropagation();

        if (invoice.afas_ornu) {
            this.confirmDialog.confirm({
                title: 'Fout',
                text: 'Deze factuur is al naar AFAS verzonden',
                confirm: 'Ok',
                cancel: null,
            });
            return;
        }

        if (invoice.execute_at && !invoice.failed_at) {
            this.confirmDialog.confirm({
                title: 'Fout',
                text: 'Deze factuur wordt al verwerkt',
                confirm: 'Ok',
                cancel: null,
            });
            return;
        }

        this.confirmDialog
            .confirm({
                title: 'Naar AFAS verzenden',
                text: `Weet u zeker dat u factuur ${invoice.od_bon_nr} naar AFAS wilt verzenden?`,
                confirm: 'Ja',
                cancel: null,
            })
            .then(() => {
                this.subscriptions.add(
                    this.invoiceService.sendToAfas(invoice.id).subscribe({
                        next: response => {
                            this.confirmDialog.confirm({
                                title: 'Succes',
                                text: response.message,
                                confirm: 'Ok',
                                cancel: null,
                            });
                            this.loadInvoices();
                        },
                        error: error => {
                            console.error('Error sending to AFAS:', error);
                            this.confirmDialog.confirm({
                                title: 'Fout',
                                text: 'Fout bij verzenden naar AFAS: ' + (error.error?.message || error.message),
                                confirm: 'Ok',
                                cancel: null,
                            });
                        },
                    })
                );
            })
            .catch(() => {
                // User cancelled
            });
    }

    sendAllToAfas() {
        this.confirmDialog
            .confirm({
                title: 'Alle facturen naar AFAS verzenden',
                text: 'Weet u zeker dat u alle gefilterde facturen naar AFAS wilt verzenden? Facturen die al voltooid of in behandeling zijn worden overgeslagen.',
                confirm: 'Ja',
            })
            .then(() => {
                const params: any = {};

                if (this.statusFilter) {
                    params.status = this.statusFilter;
                }

                if (this.fcSearch.value) {
                    params.search = this.fcSearch.value;
                }

                this.subscriptions.add(
                    this.invoiceService.sendAllToAfas(params).subscribe({
                        next: response => {
                            this.confirmDialog.confirm({
                                title: 'Succes',
                                text: response.message,
                                confirm: 'Ok',
                                cancel: null,
                            });
                            this.loadInvoices();
                        },
                        error: error => {
                            console.error('Error sending all to AFAS:', error);
                            this.confirmDialog.confirm({
                                title: 'Fout',
                                text: 'Fout bij verzenden naar AFAS: ' + (error.error?.message || error.message),
                                confirm: 'Ok',
                                cancel: null,
                            });
                        },
                    })
                );
            })
            .catch(() => {
                // User cancelled
            });
    }

    resetStatus(invoice: OrderdirectInvoice, event: Event) {
        event.stopPropagation();

        this.confirmDialog
            .confirm({
                title: 'Status resetten',
                text: `Weet u zeker dat u de status van factuur ${invoice.od_bon_nr} wilt resetten? Alle AFAS velden worden gewist.`,
                confirm: 'Ja',
                cancel: null,
            })
            .then(() => {
                this.subscriptions.add(
                    this.invoiceService.resetStatus(invoice.id).subscribe({
                        next: response => {
                            this.confirmDialog.confirm({
                                title: 'Succes',
                                text: response.message,
                                confirm: 'Ok',
                                cancel: null,
                            });
                            this.loadInvoices();
                        },
                        error: error => {
                            console.error('Error resetting status:', error);
                            this.confirmDialog.confirm({
                                title: 'Fout',
                                text: 'Fout bij resetten status: ' + (error.error?.message || error.message),
                                confirm: 'Ok',
                                cancel: null,
                            });
                        },
                    })
                );
            })
            .catch(() => {
                // User cancelled
            });
    }

    canSendToAfas(invoice: OrderdirectInvoice): boolean {
        return !invoice.afas_ornu && !(invoice.execute_at && !invoice.failed_at);
    }

    getAfasTotal(invoice: OrderdirectInvoice): number | null {
        const parsedData = this.parseInvoiceData(invoice.data);
        const elements = parsedData?.lines?.Element;

        if (Array.isArray(elements) === false || elements.length === 0) {
            return null;
        }

        const total = elements.reduce((sum: number, element: any) => {
            const fields = element?.Fields;
            const unitPrice = Number(fields?.Upri);
            const quantity = Number(fields?.QuUn);

            const hasValidUnitPrice = Number.isFinite(unitPrice);
            const hasValidQuantity = Number.isFinite(quantity);

            if (hasValidUnitPrice === false || hasValidQuantity === false) {
                return sum;
            }

            return sum + unitPrice * quantity;
        }, 0);

        return Number(total.toFixed(2));
    }

    afasMatchesSellingPrice(invoice: OrderdirectInvoice, afasTotal: number | null): boolean {
        return this.matchesSellingPrice(afasTotal, invoice.od_prijs_verk);
    }

    private parseInvoiceData(data: unknown): any {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch {
                return null;
            }
        }

        return data;
    }

    private matchesSellingPrice(afasTotal: number | null, sellingPrice: string | null): boolean {
        if (afasTotal == null || sellingPrice == null) {
            return false;
        }

        return Math.abs(afasTotal - Number(sellingPrice)) <= 0.02;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
